import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { authApi } from "../../backend_urls/authApi";
import { AuthURL } from "../../routes/auth_routes/AuthURL";
import LoadingSpinner from "../../components/loaders/LoadingSpinner";
import BaseIcon from "../../components/svg_icons/BaseIcon";

const SetNewPasswordPage = () => {
    const { uid, token } = useParams();
    const navigate = useNavigate();

    const [userData, setUserData] = useState(null);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [error, setError] = useState("");

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [formData, setFormData] = useState({ password: "", confirm_password: "" });

    useEffect(() => {
        const validateToken = async () => {
            try {
                const res = await authApi.validatePasswordToken(uid, token);
                setUserData(res.data);
            } catch (err) {
                setError(
                    err?.response?.data?.message ||
                    "This password reset link is invalid or has expired."
                );
            } finally { setLoading(false); }
        };

        validateToken();
    }, [uid, token]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value});
        setError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.password || !formData.confirm_password) {
            setError("Please fill in all fields.");
            return;
        }

        if (formData.password !== formData.confirm_password) {
            setError("Passwords do not match.");
            return;
        }

        setSubmitting(true);

        try {
            await authApi.userResetPassword({
                uid,
                token,
                password: formData.password,
                confirm_password: formData.confirm_password,
            });

            navigate(AuthURL.LOGIN, {
                replace: true,
                state: {
                    successMessage: "Your password has been updated successfully. Please sign in."
                },
            });
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                "Unable to reset password."
            );
        } finally { setSubmitting(false); }
    };

    if (loading) return <LoadingSpinner />;

    if (error && !userData) {
        return (
            <div className="login-container">
                <h1>Invalid Link</h1>
                <p>{error}</p>

                <Link
                    to={AuthURL.FORGOT_PASSWORD}
                    className="not-registered-button"
                >
                    Request another reset link
                </Link>
            </div>
        );
    }

    return (
        <>
            {submitting && <LoadingSpinner />}

            <div className="login-container">
                <h1>Create New Password</h1>

                <p>Hi <strong>{userData?.user_name}</strong>, choose a strong password for your account.</p>

                <br />

                {error && <p className="error-text">{error}</p>}

                <form onSubmit={handleSubmit}>
                    <div className="login-input-group mt-4">
                        <label className="custom-field one password-field">
                            <BaseIcon
                                name="incognito"
                                className="bi bi-incognito input-icon"
                            />

                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                placeholder=" "
                                value={formData.password}
                                onChange={handleChange}
                            />

                            <span className="placeholder">
                                New Password
                            </span>
                            <span
                                className="toggle-password-icon"
                                onClick={() => setShowPassword(!showPassword) }
                            >
                                {showPassword ? (
                                    <BaseIcon name="eyeOpen" />
                                ) : (
                                    <BaseIcon name="eyeClosed" />
                                )}
                            </span>
                        </label>

                        <br />
                        <br />

                        <label className="custom-field one password-field">
                            <BaseIcon
                                name="incognito"
                                className="bi bi-incognito input-icon"
                            />

                            <input
                                type={ showConfirmPassword ? "text" : "password" }
                                name="confirm_password"
                                placeholder=" "
                                value={formData.confirm_password}
                                onChange={handleChange}
                            />

                            <span className="placeholder">
                                Confirm Password
                            </span>

                            <span
                                className="toggle-password-icon"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? (
                                    <BaseIcon name="eyeOpen" />
                                ) : (
                                    <BaseIcon name="eyeClosed" />
                                )}
                            </span>
                        </label>

                        <br />

                        <div className="login-button-container">
                            <button
                                type="submit"
                                className="login-button"
                                disabled={submitting}
                            >
                                {submitting ? "Updating..." : "Update Password"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default SetNewPasswordPage;
